import {
  ChevronDownIcon,
  Loading01Icon,
  PlayCircleIcon,
} from '@langchain/untitled-ui-icons';
import { FormControl, Switch, Typography } from '@mui/joy';
import * as Popover from '@radix-ui/react-popover';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { LearnMoreLink } from '@/components/LearnMoreLink';
import SliderWithInput from '@/components/SliderWithInput';
import {
  Button,
  ButtonGroup,
  IconButton,
  Text,
  Tooltip,
} from '@/design-system';
import { createName } from '@/utils/create-name';

import { SaveDatasetModal } from '../PlaygroundHome/SaveDatasetModal';
import { useAlignEvaluatorInfo } from '../PlaygroundHome/hooks/useAlignEvaluator';
import {
  useDisabledStreamingText,
  useManifests,
  usePlaygroundStore,
} from '../PlaygroundHome/hooks/usePlaygroundStore';
import { useBlockRunOnEvaluatorPaneOpen } from '../utils/Playground.utils';
import { usePlaygroundSecrets } from './PlaygroundSecrets.utils';
import { usePlaygroundIsLoginNeeded } from './PlaygroundSubmit.utils';

export function PlaygroundSubmit(props: {
  onSubmit: (streaming: boolean, datasetName?: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'lg';
}) {
  const manifests = useManifests();
  const {
    prompts,
    loadingState,
    repetitions,
    isCreatingNewDataset,
    examples,
    setRepetitions,
    isStreaming: streaming,
    setIsStreaming: setStreaming,
  } = usePlaygroundStore();

  const [secrets] = usePlaygroundSecrets({ serialized: manifests });

  const showDisclaimer = usePlaygroundIsLoginNeeded(secrets, manifests); // check all the manifests, and show disclaimer if any of them need it
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const hasBeenEdited = useMemo(() => {
    return examples.some((example) => example.edited);
  }, [examples]);

  const { isAlignEvaluatorV2 } = useAlignEvaluatorInfo();

  const startButtonText = useMemo(() => {
    return hasBeenEdited || isCreatingNewDataset ? 'Save & start' : 'Start';
  }, [hasBeenEdited, isCreatingNewDataset]);

  const { onSubmit } = props;

  const [nameDatasetModalOpen, setNameDatasetModalOpen] = useState(false);
  const [datasetName, setDatasetName] = useState(createName('ds'));

  const handleSubmit = useCallback(() => {
    if (isCreatingNewDataset) {
      setNameDatasetModalOpen(true);
    } else {
      onSubmit(streaming);
    }
  }, [onSubmit, streaming, isCreatingNewDataset]);

  const { isOpen } = useBlockRunOnEvaluatorPaneOpen();
  useLayoutEffect(() => {
    function callback(event: KeyboardEvent) {
      if (
        (event.key === 'Enter' || event.key === 'NumpadEnter') &&
        (event.metaKey || event.ctrlKey) &&
        !isOpen
      ) {
        if (showDisclaimer) {
          setDisclaimerOpen(true);
        } else {
          handleSubmit();
        }
      }
    }
    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
  }, [handleSubmit, streaming, showDisclaimer, isOpen, onSubmit]);

  const isMac = useMemo(
    () => /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent),
    []
  );

  const disableStreamingText = useDisabledStreamingText({
    prompts,
    manifests,
  });

  useEffect(() => {
    if (disableStreamingText) {
      setStreaming(false);
    }
  }, [disableStreamingText, setStreaming]);

  useEffect(() => {
    if (streaming) {
      setRepetitions(1);
    }
  }, [streaming, setRepetitions]);

  if (showDisclaimer) {
    return (
      <Popover.Root open={disclaimerOpen} onOpenChange={setDisclaimerOpen}>
        <Popover.Trigger asChild>
          <StartButton
            onClick={() => setDisclaimerOpen(false)}
            disabled={props.disabled}
            text={startButtonText}
          />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="relative z-50 max-w-[400px]"
            align="end"
            sideOffset={12}
          >
            <div className="flex flex-col gap-4 rounded-lg border border-secondary bg-popover p-4">
              <div className="flex flex-col gap-1">
                <Typography fontWeight={600}>Secrets missing</Typography>
                <Typography color="neutral" fontSize="md">
                  Add a missing API key or secret in the Secrets & API keys to
                  run this playground.
                </Typography>
                <Button
                  size="sm"
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    setDisclaimerOpen(false);
                  }}
                >
                  Open Secrets & API keys
                </Button>
              </div>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  }

  if (loadingState === 'loading') {
    return (
      <Tooltip
        title={
          isMac
            ? 'Use Cmd+Enter to stop generation'
            : 'Use Ctrl+Enter to stop generation'
        }
        side="bottom"
      >
        <Button
          type="button"
          disabled={false}
          onClick={handleSubmit}
          aria-label="Stop"
        >
          <Loading01Icon className="size-4 animate-spin" />
          <Text variant="sm" weight="normal">
            Stop
          </Text>
        </Button>
      </Tooltip>
    );
  }

  const mainButton = (
    <Button
      leftDecorator={PlayCircleIcon}
      disabled={props.disabled}
      size={props.size === 'lg' ? 'md' : 'sm'}
      onClick={handleSubmit}
      data-first-child
    >
      {startButtonText}
    </Button>
  );

  const mainButtonGroup = (
    <ButtonGroup variant="normal" color="primary">
      <Tooltip
        title={
          isMac
            ? 'Use Cmd+Enter to start generation'
            : 'Use Ctrl+Enter to start generation'
        }
        side="bottom"
      >
        {mainButton}
      </Tooltip>
      <Popover.Trigger asChild disabled={props.disabled}>
        <IconButton
          icon={ChevronDownIcon}
          label="Open settings"
          size={props.size === 'lg' ? 'md' : 'sm'}
        />
      </Popover.Trigger>
    </ButtonGroup>
  );

  return (
    <>
      <Popover.Root>
        {isAlignEvaluatorV2 ? mainButton : mainButtonGroup}
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={5} className="relative z-10">
            <div className="flex max-w-[400px] flex-col space-y-3 rounded-lg border border-secondary bg-popover p-3 shadow-lg">
              <SliderWithInput
                title="Repetitions"
                description={
                  <Text variant="sm" className="text-quaternary">
                    Run the same prompt multiple times to reduce variability in
                    results.{' '}
                    <LearnMoreLink link="https://docs.langchain.com/langsmith/repetition" />
                  </Text>
                }
                value={repetitions ?? 1}
                onChange={(value) => {
                  if (value != null) {
                    if (value > 1) {
                      setStreaming(false);
                    }
                    setRepetitions(value);
                  }
                }}
                min={1}
                max={30}
                step={1}
              />

              <FormControl orientation="horizontal">
                <div className="mr-4 flex flex-col gap-1">
                  <Text weight="medium">Enable streaming</Text>
                  <Text variant="sm" className="text-quaternary">
                    When enabled, you'll see the response appear word by word in
                    real-time. When disabled, you'll see the complete response
                    all at once.
                  </Text>
                </div>
                <Tooltip title={disableStreamingText}>
                  <div className="my-auto">
                    <Switch
                      title="Enable streaming"
                      checked={streaming}
                      disabled={!!disableStreamingText}
                      onChange={() => setStreaming(!streaming)}
                    />
                  </div>
                </Tooltip>
              </FormControl>

              {repetitions > 1 && (
                <Text variant="xs" className="italic text-secondary">
                  Cannot stream while running repetitions
                </Text>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <SaveDatasetModal
        open={nameDatasetModalOpen}
        onClose={() => setNameDatasetModalOpen(false)}
        onSave={() => {
          onSubmit(streaming, datasetName);
          setNameDatasetModalOpen(false);
        }}
        isLoading={false}
        datasetName={datasetName}
        setDatasetName={setDatasetName}
      />
    </>
  );
}

function StartButton(props: {
  onClick: () => void;
  disabled?: boolean;
  text: string;
}) {
  return (
    <Button
      leftDecorator={PlayCircleIcon}
      onClick={props.onClick}
      disabled={props.disabled}
      size="sm"
    >
      {props.text}
    </Button>
  );
}
